/**
 * SEO 工具函数
 * 从 fulltext 接口数据中读取 SEO 信息，支持多语言
 */
import { getFulltextData } from './i18n'
import { getLanguage } from './lang'

// 默认 SEO
const defaultSeo = {
  title: 'Jingsun Car - China Used Cars Export',
  description: 'Export quality used cars from China at unbeatable prices.',
  keywords: 'china used cars, car export, buy car from china'
}

// 根据路由名获取 SEO 配置 key
function getSeoKeyByRoute(route) {
  const name = route.name?.toLowerCase() || ''
  
  const keyMap = {
    'home': 'home',
    'blog': 'blog',
    'blogDetail': 'blogDetail',
    'faq': 'faq',
	'page': 'page',
    'about': 'about',
    'detail': 'detail',
    'favorites': 'favorites',
    'userAgreement': 'userAgreement'
  }
  
  return keyMap[name] || keyMap[name.replace('H5', '')] || null
}

// 格式化文本，替换占位符 {key}
function formatText(text, params = {}) {
  if (!text) return ''
  let result = text
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value || '')
  }
  return result
}

// 更新页面 SEO（用于通用页面）
function updateSeo(route) {
  const fulltextData = getFulltextData()
  const seoConfig = fulltextData?.seo || {}
  const seoKey = getSeoKeyByRoute(route)
  
  // 获取 SEO 配置
  let title, description, keywords
  
  // 如果有 fulltext.seo.{page} 配置，使用它
  if (seoKey && seoConfig[seoKey]) {
    const pageConfig = seoConfig[seoKey]
    title = pageConfig.title
    description = pageConfig.description
    keywords = pageConfig.keywords
  }
  
  // 否则使用 fulltext 通用字段
  if (!title) {
    title = fulltextData?.title || defaultSeo.title
  }
  if (!description) {
    description = fulltextData?.description || defaultSeo.description
  }
  if (!keywords) {
    keywords = fulltextData?.keywords || defaultSeo.keywords
  }
  
  // 更新 document.title
  if (title) {
    document.title = title
  }
  
  // 更新 meta 标签
  updateMetaTag('description', description)
  updateMetaTag('keywords', keywords)
  
  // 更新 og: 标签
  updateOgTag('og:title', title)
  updateOgTag('og:description', description)
  updateOgTag('og:url', window.location.href)
  updateOgTag('og:type', seoKey === 'blogDetail' ? 'article' : 'website')
}

// 更新 meta 标签
function updateMetaTag(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', name)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content || '')
}

// 更新 og: meta 标签
function updateOgTag(property, content) {
  let meta = document.querySelector(`meta[property="${property}"]`)
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('property', property)
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content || '')
}

// 需要注入 canonical 的 SEO 主页面（收藏页/跳转页等非索引页不注入）
const canonicalRoutes = ['Home', 'Detail', 'About', 'Page', 'Faq', 'Blog', 'BlogDetail', 'UserAgreement']

// 更新 canonical 标签
// 规则：canonical = 当前域名 + 路由 path（不含 query）。
// 详情/博客等干净路径指向自身；首页等带筛选参数（country/keyword/page）的列表页归并到无参数基础 URL。
function updateCanonical(route) {
  // 无论目标路由是否在注入白名单，先移除旧标签，
  // 避免从 SEO 主页面切换到收藏页/跳转页等非索引页后残留上一页的 canonical
  const oldLink = document.querySelector('link[rel="canonical"]')
  if (oldLink) {
    oldLink.remove()
  }

  if (!route || !route.name || !canonicalRoutes.includes(route.name)) {
    return
  }

  // 多语言站点各自独立域名部署，用 window.location.origin 自动适配当前站点
  const canonicalUrl = window.location.origin + route.path
  const link = document.createElement('link')
  link.setAttribute('rel', 'canonical')
  link.setAttribute('href', canonicalUrl)
  document.head.appendChild(link)
}

// 非索引页面（收藏页/跳转页等功能页），加 noindex 防止被搜索引擎收录
const noindexRoutes = ['Favorites', 'Redirect']

// 更新 noindex 标记：目标路由在 noindexRoutes 时写入 noindex，离开时移除避免 SPA 切换残留
function updateNoindex(route) {
  let meta = document.querySelector('meta[name="robots"]')
  const needNoindex = route && route.name && noindexRoutes.includes(route.name)

  if (needNoindex) {
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'robots')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', 'noindex, nofollow')
  } else if (meta && /noindex/.test(meta.getAttribute('content') || '')) {
    // 从非索引页导航回正常页面时，移除 noindex，避免残留
    meta.remove()
  }
}

// 注入 JSON-LD 结构化数据（幂等：先移除旧的再注入，避免 SPA 路由切换后残留/重复）
function injectJsonLd(data, id = 'seo-jsonld') {
  removeJsonLd(id)
  if (!data) return
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.id = id
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

// 移除指定 JSON-LD 脚本
function removeJsonLd(id = 'seo-jsonld') {
  const old = document.getElementById(id)
  if (old) old.remove()
}

// 注入面包屑结构化数据（BreadcrumbList）
// items: [{ name, url }]，如 [{ name: 'Home', url: '/' }, { name: 'FAQ', url: '/faq' }]
function injectBreadcrumbJsonLd(items) {
  if (!items || !items.length) {
    removeJsonLd('seo-breadcrumb')
    return
  }
  injectJsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url
    }))
  }, 'seo-breadcrumb')
}

// 品牌实体信息（GEO：让 AI/搜索引擎把主站与引流站识别为同一家公司）
// 主站 www.sin-auto.com（本站，en/fr/es/ar 各自独立域名部署）
// 引流站 www.jingsuncar.com（第三方平台站，同一主体）
// 联系方式取自全站配置 /contact/chat_list，非手写
const BRAND = {
  name: 'Jingsun Car',
  legalName: 'Jingsun Car Co., Ltd',
  alternateName: ['Jingsun', 'JINGSUN', 'sin-auto'],
  sameAs: [
    'https://www.sin-auto.com/',
    'https://www.jingsuncar.com/'
  ],
  telephone: '+8613225612350',
  email: 'gekko@jingsuncar.com'
}

// 注入品牌实体结构化数据（Organization + WebSite）
// 四个语言站各自独立域名部署，url 用 window.location.origin 自动适配当前站点
// 幂等：沿用 injectJsonLd 的 id 机制，SPA 路由切换不会重复堆积
function injectBrandJsonLd() {
  const origin = window.location.origin
  injectJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': origin + '/#organization',
        name: BRAND.name,
        legalName: BRAND.legalName,
        alternateName: BRAND.alternateName,
        url: origin + '/',
        sameAs: BRAND.sameAs,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'sales',
          telephone: BRAND.telephone,
          email: BRAND.email,
          availableLanguage: ['en', 'fr', 'es', 'ar', 'zh']
        }
      },
      {
        '@type': 'WebSite',
        '@id': origin + '/#website',
        url: origin + '/',
        name: BRAND.name,
        publisher: {
          '@id': origin + '/#organization'
        }
      }
    ]
  }, 'seo-brand')
}

// 注入文章结构化数据（BlogPosting）+ 面包屑，供博客详情页调用
// news 为 page/news_info 的返回体；文章自身没有 description 时用 fallbackDescription 兜底
function injectArticleJsonLd(news, fallbackDescription = '') {
  if (!news) {
    removeJsonLd('seo-article')
    removeJsonLd('seo-breadcrumb')
    return
  }

  const origin = window.location.origin
  const url = origin + window.location.pathname
  const description = news.description || news.excerpt || fallbackDescription || ''

  const node = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': url + '#article',
    mainEntityOfPage: url,
    url: url,
    headline: news.title || '',
    // 文章主图（单一 URL，用数组更兼容）
    image: news.image ? [news.image] : undefined,
    // 后端给的是 'YYYY-MM-DD HH:mm:ss'，统一成 ISO 8601
    datePublished: (news.created_at || '').replace(' ', 'T'),
    inLanguage: getLanguage() || 'en',
    articleSection: news.category_name || undefined,
    description: description || undefined,
    // author 用接口返回的值（后台可随时改成 jingsun car 或其它实体），为空才回落到品牌名。
    // 故意不绑 @id：作者未来可能是别的实体，硬绑到本站 Organization 会张冠李戴。
    author: {
      '@type': 'Organization',
      name: news.author || BRAND.name
    },
    // publisher 固定是本站主体，保留 @id 与首页 Organization 连成一体
    publisher: {
      '@id': origin + '/#organization'
    }
  }

  // 去掉空值，避免输出 datePublished:"" 这类无效字段
  Object.keys(node).forEach((k) => {
    if (node[k] === undefined || node[k] === '') delete node[k]
  })

  injectJsonLd(node, 'seo-article')

  injectBreadcrumbJsonLd([
    { name: 'Home', url: origin + '/' },
    { name: 'Blog', url: origin + '/blog' },
    { name: news.title || '', url: url }
  ])
}

export {
  updateSeo,
  updateCanonical,
  updateNoindex,
  injectJsonLd,
  removeJsonLd,
  injectBreadcrumbJsonLd,
  injectBrandJsonLd,
  injectArticleJsonLd
}
